import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"

import useSlugExtractor from "@hooks/useSlugExtractor"

import { faIcon } from "@utils/faIcon"

import AccessCheck from "@particles/AccessCheck/AccessCheck"

import { HOME_ROUTE } from "@routes/home"
import { JOBS_ROUTES } from "@routes/jobs"
import { MENU_ROUTE } from "@routes/menu"
import { SEARCH_ROUTE } from "@routes/search"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import BottomBarItemBlank from "./BottomBarItemBlank/BottomBarItemBlank"
import styles from "./NavBottomBar.module.scss"
import NavBottomBarCreateObject from "./NavBottomBarCreateObject/NavBottomBarCreateObject"
import NavBottomBarItem from "./NavBottomBarItem/NavBottomBarItem"

const body = document.querySelector("body")

export default function NavBottomBar() {
    const bottomBarRef = useRef<HTMLDivElement>(null)

    const [homeRoute, jobsListRoute, menuRoute, searchRoute] = useSlugExtractor([
        HOME_ROUTE,
        JOBS_ROUTES.LIST,
        MENU_ROUTE,
        SEARCH_ROUTE,
    ])

    useEffect(() => {
        if (bottomBarRef.current) {
            const bottomBarHeight = bottomBarRef.current.offsetHeight

            const root = document.documentElement
            root.style.setProperty("--bottom-bar-height", `${bottomBarHeight}px`)
        }

        return () => {
            const root = document.documentElement
            root.style.setProperty("--bottom-bar-height", `0`)
        }
    }, [bottomBarRef])

    return (
        body &&
        createPortal(
            <div ref={bottomBarRef} className={styles.base} role="navigation">
                <div className={styles.container}>
                    <NavBottomBarItem
                        title="Home"
                        path={homeRoute}
                        icon={faIcon({ name: "house", style: "regular" })}
                        iconActive={faIcon({ name: "house", style: "solid" })}
                    />
                    <AccessCheck
                        entitlements={["entitlement_jobs_enabled"]}
                        permissions={{
                            jobs_list_permission: PERMISSION_LEVEL.FULL,
                        }}
                        placeholder={<BottomBarItemBlank />}
                    >
                        <NavBottomBarItem
                            title={objectMap.job.labelPlural}
                            path={jobsListRoute}
                            icon={objectMap.job.icon}
                            iconActive={objectMap.job.iconActive}
                        />
                    </AccessCheck>

                    <AccessCheck
                        permissions={{
                            estimates_create_permission: PERMISSION_LEVEL.RESTRICTED,
                            jobs_create_permission: PERMISSION_LEVEL.RESTRICTED,
                            invoices_create_permission: PERMISSION_LEVEL.RESTRICTED,
                            purchase_orders_create_permission: PERMISSION_LEVEL.RESTRICTED,
                            vendors_create_permission: PERMISSION_LEVEL.FULL,
                            clients_create_permission: PERMISSION_LEVEL.FULL,
                            pricebook_create_permission: PERMISSION_LEVEL.FULL,
                        }}
                    >
                        <NavBottomBarCreateObject />
                    </AccessCheck>

                    <AccessCheck
                        permissions={{
                            estimates_list_permission: PERMISSION_LEVEL.FULL,
                            jobs_list_permission: PERMISSION_LEVEL.FULL,
                            invoices_list_permission: PERMISSION_LEVEL.FULL,
                            purchase_orders_list_permission: PERMISSION_LEVEL.FULL,
                            bills_list_permission: PERMISSION_LEVEL.FULL,
                            vendors_list_permission: PERMISSION_LEVEL.FULL,
                            clients_list_permission: PERMISSION_LEVEL.FULL,
                            pricebook_list_permission: PERMISSION_LEVEL.FULL,
                        }}
                        placeholder={<BottomBarItemBlank />}
                    >
                        <NavBottomBarItem
                            title="Search"
                            path={searchRoute}
                            icon={faIcon({ name: "magnifying-glass", style: "regular" })}
                            iconActive={faIcon({ name: "magnifying-glass", style: "solid" })}
                        />
                    </AccessCheck>
                    <NavBottomBarItem
                        title="Menu"
                        path={menuRoute}
                        icon={faIcon({ name: "list-ul", style: "regular" })}
                        iconActive={faIcon({ name: "list-ul", style: "solid" })}
                    />
                </div>
            </div>,
            body,
        )
    )
}
